import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { CoreDeviceListRef } from "@gf-core/widgets/types/CoreDeviceListRef";
import CoreDeviceList from "@gf-core/widgets/widgets/CoreDeviceList";
import { DeviceTabTypeEnum } from "../../../../types/DeviceTabTypeEnum";
import { useStore } from "../../../../store/store";
import { defaultColumns } from "../../../../utils/columnConfigs/deviceListConfig";
import { routes } from "../../../../utils/routeHelper";
import { ListWrapper } from "./DevicesTab.styles";

interface Props {}

const DevicesTab = forwardRef<CoreDeviceListRef, Props>((_, ref) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const showAppStoreFeature = useStore.useShowAppStoreFeature();
  const { accountId, installationId } = useParams();
  const deviceListRef = useRef<CoreDeviceListRef>(null);

  useImperativeHandle(
    ref,
    () => ({ ...(deviceListRef.current as CoreDeviceListRef) }),
    [deviceListRef.current],
  );

  const columns = useMemo(
    () =>
      defaultColumns(
        true,
        installationId ?? "",
        () => deviceListRef.current?.refetch(),
        (productId: string) => navigate(routes.product.set(productId)),
        true,
        true,
        deviceListRef.current?.data?.model.some(
          (device) => !!device.capabilities,
        ) ?? false,
        t,
        showAppStoreFeature ? accountId : undefined,
      ),
    [
      installationId,
      deviceListRef.current?.data,
      t,
      showAppStoreFeature,
      accountId,
      navigate,
    ],
  );

  return (
    <ListWrapper>
      <CoreDeviceList
        key={installationId}
        columns={columns}
        installationId={installationId ?? ""}
        isExpanded
        withSpacing
        cellStyles={{ height: 48 }}
        rowStyles={{ borderBottom: "transparent" }}
        ref={deviceListRef}
        onRowClick={(row) => {
          navigate(
            routes.device.set(
              accountId ?? "",
              installationId ?? "",
              row.original.deviceTwinId,
              DeviceTabTypeEnum.Overview,
            ),
          );
        }}
      />
    </ListWrapper>
  );
});

export default DevicesTab;
